import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IGateEventDto, ILockerEventDto } from 'api/interfaces/events';
import { GateServiceAPI } from 'api/services/gateServiceAPI';
import { LockerServiceAPI } from 'api/services/lockerServiceAPI';
import { CameraPreviewButton } from 'components/shared/CameraPreviewButton';
import { DictGateEventType, DictLockerEventType, formatDateTimeWithSeconds } from 'shared';

const EVENTS_PAGE_SIZE = 20;

// Company names run long enough to push the camera button off the row, so the actor is capped the way
// the tiles cap it. The CSS ellipsis below is the backstop for a single unbroken 20-character word.
const ACTOR_NAME_MAX_LENGTH = 20;

const truncateActorName = (name: string) =>
	name.length > ACTOR_NAME_MAX_LENGTH ? `${name.slice(0, ACTOR_NAME_MAX_LENGTH)}…` : name;

const lockerEventLabelKeys: Partial<Record<DictLockerEventType, string>> = {
	[DictLockerEventType.PinRejected]: 'pinRejected',
	[DictLockerEventType.PinExpired]: 'pinExpired',
	[DictLockerEventType.Opened]: 'opened',
	[DictLockerEventType.Closed]: 'closed',
	[DictLockerEventType.ForceOpened]: 'forceOpened',
	[DictLockerEventType.LockerLocked]: 'lockerLocked',
	[DictLockerEventType.NoCommunication]: 'noCommunication',
	[DictLockerEventType.ReadyToOpen]: 'readyToOpen',
	[DictLockerEventType.Timeout]: 'timeout',
	[DictLockerEventType.LockerCommandSent]: 'commandSent',
	[DictLockerEventType.LockerArmed]: 'armed',
	[DictLockerEventType.LockerDisarmed]: 'disarmed',
	[DictLockerEventType.MSpaceLockOpened]: 'mSpaceLockOpened',
	[DictLockerEventType.MSpaceLockNotOpened]: 'mSpaceLockNotOpened',
	[DictLockerEventType.TTLockOpened]: 'ttLockOpened',
	[DictLockerEventType.TTLockNotOpened]: 'ttLockNotOpened',
};

const gateEventLabelKeys: Partial<Record<DictGateEventType, string>> = {
	[DictGateEventType.PinAccepted]: 'pinAccepted',
	[DictGateEventType.PinRejected]: 'pinRejected',
	[DictGateEventType.Opened]: 'opened',
	[DictGateEventType.Closed]: 'closed',
	[DictGateEventType.ForceOpened]: 'forceOpened',
	[DictGateEventType.GateCommandSent]: 'commandSent',
	[DictGateEventType.LicensePlateAccepted]: 'licensePlateAccepted',
	[DictGateEventType.LicensePlateRejected]: 'licensePlateRejected',
	[DictGateEventType.MSpaceLockOpened]: 'mSpaceLockOpened',
	[DictGateEventType.MSpaceLockNotOpened]: 'mSpaceLockNotOpened',
	[DictGateEventType.TTLockOpened]: 'ttLockOpened',
	[DictGateEventType.TTLockNotOpened]: 'ttLockNotOpened',
};

interface ItemEvent {
	id: string;
	label: string;
	dateTime: string;
	actorName?: string | null;
}

interface ItemEventsProps {
	itemId: string;
	isLocker: boolean;
	locationId: string;
	showCameraPreview: boolean;
}

const EventRowSkeleton = ({ withAction }: { withAction: boolean }) => (
	<Box
		sx={{
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			gap: 1,
			px: 1.5,
			py: 1.25,
			borderTop: 1,
			borderColor: 'borders.primary',
		}}
	>
		<Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 0.25, flexGrow: 1, minWidth: 0 }}>
			<Skeleton variant='text' width='45%' sx={{ fontSize: '0.875rem' }} />
			<Box sx={{ display: 'flex', columnGap: 1, minWidth: 0 }}>
				<Skeleton variant='text' width='40%' sx={{ fontSize: '0.75rem' }} />
				<Skeleton variant='text' width='30%' sx={{ fontSize: '0.75rem' }} />
			</Box>
		</Box>
		{withAction && <Skeleton variant='rounded' width={110} height={30} sx={{ flexShrink: 0 }} />}
	</Box>
);

export const ItemEvents = ({ itemId, isLocker, locationId, showCameraPreview }: ItemEventsProps) => {
	const { t } = useTranslation();
	const [events, setEvents] = useState<ItemEvent[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [failed, setFailed] = useState(false);
	const loadedPagesRef = useRef(0);
	const sentinelRef = useRef<HTMLDivElement | null>(null);

	const mapLocker = useCallback(
		(event: ILockerEventDto): ItemEvent => ({
			id: event.id,
			label: t(`enums.lockerEvent.${lockerEventLabelKeys[event.eventType]}`, {
				defaultValue: String(event.eventType),
			}),
			dateTime: event.dateTime,
			actorName: event.actorName,
		}),
		[t]
	);

	const mapGate = useCallback(
		(event: IGateEventDto): ItemEvent => ({
			id: event.id,
			label: t(`enums.gateEvent.${gateEventLabelKeys[event.dictAccessEventType]}`, {
				defaultValue: String(event.dictAccessEventType),
			}),
			dateTime: event.eventDateUTC,
			actorName: event.actorName,
		}),
		[t]
	);

	const loadPage = useCallback(
		async (pageNumber: number) => {
			const search = { pageNumber, itemsPerPage: EVENTS_PAGE_SIZE };

			try {
				const response = isLocker
					? await LockerServiceAPI.getEvents(itemId, search)
					: await GateServiceAPI.getEvents(itemId, search);

				if (!response.success) {
					setFailed(true);
					return;
				}

				const mapped = response.data.items.map((item) =>
					isLocker ? mapLocker(item as ILockerEventDto) : mapGate(item as IGateEventDto)
				);

				loadedPagesRef.current = pageNumber;
				setTotalCount(response.data.totalItemsCount);
				setEvents((prev) => (pageNumber === 1 ? mapped : [...prev, ...mapped]));
			} catch {
				setFailed(true);
			}
		},
		[isLocker, itemId, mapGate, mapLocker]
	);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			await loadPage(1);
			if (!cancelled) setIsLoading(false);
		};

		load();

		return () => {
			cancelled = true;
		};
	}, [loadPage]);

	const hasMore = events.length < totalCount;

	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!sentinel || !hasMore || isLoading || isLoadingMore) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (!entries[0].isIntersecting) return;

				setIsLoadingMore(true);
				loadPage(loadedPagesRef.current + 1).finally(() => setIsLoadingMore(false));
			},
			{ rootMargin: '120px' }
		);

		observer.observe(sentinel);

		return () => observer.disconnect();
	}, [hasMore, isLoading, isLoadingMore, loadPage]);

	const card = (children: React.ReactNode) => (
		<Box sx={{ mt: 2 }}>
			<Typography variant='subtitle2' sx={{ fontWeight: 700, textTransform: 'uppercase', mb: 1 }}>
				{t('pages.alerts.itemDetails.events')}
			</Typography>

			<Box sx={{ border: 1, borderColor: 'borders.primary', borderRadius: 2, overflow: 'hidden' }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 1,
						px: 1.5,
						py: 1,
						backgroundColor: 'highlighted.secondary',
					}}
				>
					<Typography variant='caption' sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
						{t('pages.alerts.itemDetails.eventsColumns')}
					</Typography>
					{showCameraPreview && (
						<Typography variant='caption' sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
							{t('pages.alerts.itemDetails.eventsAction')}
						</Typography>
					)}
				</Box>
				{children}
			</Box>
		</Box>
	);

	if (isLoading) {
		return card(
			<>
				{Array.from({ length: 2 }, (_, index) => (
					<EventRowSkeleton key={index} withAction={showCameraPreview} />
				))}
			</>
		);
	}

	if (failed) return null;

	if (!events.length) {
		return card(
			<Box sx={{ px: 1.5, py: 2, borderTop: 1, borderColor: 'borders.primary' }}>
				<Typography variant='body2' color='text.secondary' textAlign='center'>
					{t('pages.alerts.itemDetails.eventsEmpty')}
				</Typography>
			</Box>
		);
	}

	return card(
		<>
			{events.map((event) => (
				<Box
					key={event.id}
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 1,
						px: 1.5,
						py: 1.25,
						borderTop: 1,
						borderColor: 'borders.primary',
					}}
				>
					{/* Two fixed lines - the event name, then time and actor - instead of one wrapping run.
					    Letting all three share a line meant the break landed wherever the text happened to
					    run out, which differed per row and split the timestamp. */}
					<Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 0.25, minWidth: 0 }}>
						<Typography variant='body2' fontWeight={700}>
							{event.label}
						</Typography>
						<Box sx={{ display: 'flex', alignItems: 'baseline', columnGap: 1, flexWrap: 'wrap', minWidth: 0 }}>
							{/* nowrap keeps the date whole; when the actor no longer fits beside it, the actor
							    wraps to its own line rather than the date breaking apart. */}
							<Typography
								variant='caption'
								color='text.secondary'
								sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
							>
								{formatDateTimeWithSeconds(event.dateTime)}
							</Typography>
							{event.actorName && (
								<Typography
									variant='caption'
									color='text.secondary'
									title={event.actorName}
									sx={{
										minWidth: 0,
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									}}
								>
									{truncateActorName(event.actorName)}
								</Typography>
							)}
						</Box>
					</Box>

					{showCameraPreview && (
						<Box sx={{ flexShrink: 0 }}>
							<CameraPreviewButton
								locationId={locationId}
								queryParams={isLocker ? { lockerEventId: event.id } : { gateEventId: event.id }}
								size='small'
							/>
						</Box>
					)}
				</Box>
			))}

			{hasMore && (
				<Box ref={sentinelRef}>
					<EventRowSkeleton withAction={showCameraPreview} />
					<EventRowSkeleton withAction={showCameraPreview} />
				</Box>
			)}
		</>
	);
};
